/**
 * RV Rent Pro SDK - API Module
 * Handles all API requests
 *
 * All SDK endpoints are idempotent GET reads, so requests are:
 *  - retried with exponential backoff on transient network/5xx failures, and
 *  - de-duplicated while in flight (concurrent identical requests share one promise).
 * Callers may pass an AbortSignal to stop caring about a superseded request; the
 * shared in-flight request is NOT torn down (so it can't break another caller that
 * is sharing it), it simply rejects the aborting caller with an AbortError.
 *
 * NOTE: there is intentionally NO response cache here. Cross-time caching of
 * availability/pricing is deferred to a server-side Redis layer at the route.
 * See docs/sdk-data-fetching-review.md.
 */

import { getFullUrl, SDK_API_VERSION } from '../config';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const RETRYABLE_STATUS = new Set([500, 502, 503, 504]);

// Module-scoped so every initApi() instance shares the same in-flight map.
const inFlightRequests = new Map<string, Promise<any>>();

// Module-scoped registry of each RV's photo fingerprint (photosVersion), recorded from
// essential-data responses. fetchPhotosByRvId appends it as ?v= so that a photo change
// (which yields a new fingerprint) busts the long-CDN-cached photos response without any
// server-side purge: new fingerprint = new URL = new cache key = fresh fetch. Until an
// RV's version is known, the photos request simply omits ?v= and falls back to the
// existing cached behavior: safe degradation, never an error.
const photoVersionByRvId = new Map<string, string>();

function recordPhotoVersions(rvs: unknown): void {
  if (!Array.isArray(rvs)) {
    return;
  }
  for (const rv of rvs) {
    if (
      rv &&
      typeof rv.id === 'string' &&
      typeof rv.photosVersion === 'string' &&
      rv.photosVersion
    ) {
      photoVersionByRvId.set(rv.id, rv.photosVersion);
    }
  }
}

function createAbortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function backoffDelay(attempt: number): Promise<void> {
  // Exponential backoff: 1s, 2s, 4s. Awaited inline between retry attempts.
  const ms = INITIAL_RETRY_DELAY_MS * 2 ** (attempt - 1);
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Fetch JSON with retry on transient network errors and 5xx responses.
 * No AbortSignal here on purpose: this runs as the shared in-flight request.
 */
async function fetchJsonWithRetry(url: string): Promise<any> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(getFullUrl(url), {
        headers: {
          Origin: typeof window !== 'undefined' ? window.location.origin : '',
        },
      });

      if (!response.ok) {
        if (RETRYABLE_STATUS.has(response.status) && attempt < MAX_RETRIES) {
          await backoffDelay(attempt);
          continue;
        }
        throw new Error(`Request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error;
      // fetch() rejects with a TypeError on network failure, so retry those.
      // HTTP errors are already handled above and are not retried again here.
      const isNetworkError = error instanceof TypeError;
      if (isNetworkError && attempt < MAX_RETRIES) {
        await backoffDelay(attempt);
        continue;
      }
      throw error;
    }
  }

  throw lastError;
}

/**
 * Wrap a shared promise so an aborting caller stops waiting (rejects with
 * AbortError) without disturbing the shared request or any other caller.
 */
function raceWithSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) {
    return promise;
  }
  if (signal.aborted) {
    return Promise.reject(createAbortError());
  }
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(createAbortError());
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}

/**
 * De-duplicated, retrying fetch. Concurrent identical URLs share one request.
 */
function dedupedFetch(url: string, signal?: AbortSignal): Promise<any> {
  // Stamp the contract version on every request (all SDK URLs already carry a query
  // string, so always append with '&'). Lets the server serve the right response shape.
  const versionedUrl = `${url}&sdkVersion=${encodeURIComponent(SDK_API_VERSION)}`;
  let shared = inFlightRequests.get(versionedUrl);
  if (shared === undefined) {
    shared = fetchJsonWithRetry(versionedUrl).finally(() => {
      inFlightRequests.delete(versionedUrl);
    });
    inFlightRequests.set(versionedUrl, shared);
  }
  return raceWithSignal(shared, signal);
}

/**
 * Initialize the API module
 * @returns API module instance
 */
export function initApi() {
  /**
   * Fetch photos for a specific RV using secure SDK endpoint
   * @param rvId - RV ID
   * @param apiKey - API key for authentication
   * @param options - Optional parameters
   * @param signal - Optional AbortSignal to cancel interest in the request
   * @returns Photos data
   */
  async function fetchPhotosByRvId(
    rvId: string,
    apiKey: string,
    options: {
      category?: string;
      pageIndex?: number;
      perPage?: number;
    } = {},
    signal?: AbortSignal,
  ) {
    const params = new URLSearchParams();
    params.append('apiKey', apiKey);
    params.append('rvId', rvId);

    if (options.category) {
      params.append('category', options.category);
    }

    if (options.pageIndex !== undefined) {
      params.append('pageIndex', options.pageIndex.toString());
    }

    if (options.perPage !== undefined) {
      params.append('perPage', options.perPage.toString());
    }

    // Cache-buster: a photo change yields a new fingerprint (recorded from essential-data),
    // which changes this URL and forces a fresh fetch past the long CDN cache. The route
    // ignores `v`; it exists only to vary the cache key. Omitted until the version is
    // known (degrades to existing cached behavior).
    const version = photoVersionByRvId.get(rvId);
    if (version) {
      params.append('v', version);
    }

    const url = `/public/api/checkout/sdk/photos?${params.toString()}`;

    try {
      const data = await dedupedFetch(url, signal);

      return {
        success: data.success,
        data: data.data,
        meta: data.meta,
        errors: data.errors,
        hasErrors: data.hasErrors,
      };
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching photos via SDK endpoint:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: [],
      };
    }
  }

  /**
   * Fetch full RV details: settings, pricing rules, disabled dates, and addons.
   * Date-independent (disabled dates use a fixed forward window and addons are filtered
   * client-side), so this is fetched once per RV (no refetch on date change).
   * @param lookup - The unit's uuid, or the per-org unit number a share link carries
   * @param apiKey - API key for authentication
   * @param signal - Optional AbortSignal to cancel interest in the request
   * @returns Complete RV details with settings
   */
  async function fetchSdkRvDetails(
    lookup: { rvId: string } | { unit: string },
    apiKey: string,
    signal?: AbortSignal,
  ) {
    const params = new URLSearchParams();
    params.append('apiKey', apiKey);
    if ('rvId' in lookup) {
      params.append('rvId', lookup.rvId);
    } else {
      params.append('unit', lookup.unit);
    }

    const url = `/public/api/checkout/sdk/rv-details?${params.toString()}`;

    try {
      const data = await dedupedFetch(url, signal);

      return {
        success: data.success,
        data: data.data,
        meta: data.meta,
        errors: data.errors,
        hasErrors: data.hasErrors,
      };
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching SDK RV details:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: null,
      };
    }
  }

  /**
   * Fetch SDK list data (RVs + settings + pricing rules + class types). When a date
   * range is supplied the list is availability-filtered. This is the single list
   * endpoint; it serves both the no-dates first paint and the dated/date-change cases.
   * @param apiKey - API key for authentication
   * @param options - Optional date range for availability filtering
   * @param signal - Optional AbortSignal to cancel interest in the request
   * @returns SDK list data for immediate display
   */
  async function fetchEssentialData(
    apiKey: string,
    options?: { from?: string; to?: string },
    signal?: AbortSignal,
  ) {
    const params = new URLSearchParams();
    params.append('apiKey', apiKey);
    if (options?.from) {
      params.append('from', options.from);
    }
    if (options?.to) {
      params.append('to', options.to);
    }
    const url = `/public/api/checkout/sdk/essential-data?${params.toString()}`;

    try {
      const data = await dedupedFetch(url, signal);

      // Record each RV's photo fingerprint so fetchPhotosByRvId can cache-bust on change.
      recordPhotoVersions(data?.data?.rvs);

      return {
        success: data.success,
        data: data.data,
        meta: data.data?.meta,
      };
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching essential data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: null,
      };
    }
  }

  /**
   * Fetch all initial data needed for SDK in a single optimized request
   * @param apiKey - API key for authentication
   * @param options - Optional parameters for filtering
   * @param signal - Optional AbortSignal to cancel interest in the request
   * @returns Combined initial data (RVs, settings, pricing rules, class types)
   */
  async function fetchInitialData(
    apiKey: string,
    options?: {
      from?: string;
      to?: string;
    },
    signal?: AbortSignal,
  ) {
    const params = new URLSearchParams();
    params.append('apiKey', apiKey);

    if (options?.from) {
      params.append('from', options.from);
    }

    if (options?.to) {
      params.append('to', options.to);
    }

    const queryString = params.toString();
    const url = `/public/api/checkout/sdk/initial-data?${queryString}`;

    try {
      const data = await dedupedFetch(url, signal);

      // RV data is already transformed to camelCase by the RPC function
      return {
        success: data.success,
        data: data.data,
        meta: data.data?.meta,
      };
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching initial data:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: null,
      };
    }
  }

  /**
   * Fetch addons for an organization.
   * LEGACY/FALLBACK: addons are now folded into the rv-details response, so the live
   * detail path no longer calls this. Retained only as the `useRvPriceCalculation`
   * fallback when addons aren't supplied (and for old embeds). Uses the organizationUuid
   * auth model rather than apiKey, another reason to retire it once embeds migrate.
   * @param organizationUuid - Organization UUID
   * @param signal - Optional AbortSignal to cancel interest in the request
   * @returns Addons data
   */
  async function fetchAddons(organizationUuid: string, signal?: AbortSignal) {
    const url = `/public/api/checkout/addons?organizationUuid=${encodeURIComponent(organizationUuid)}`;

    try {
      const data = await dedupedFetch(url, signal);

      return {
        success: data.success,
        data: data.data,
        errors: data.errors,
        hasErrors: data.hasErrors,
      };
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      console.error('Error fetching addons:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: [],
      };
    }
  }

  return {
    fetchPhotosByRvId,
    fetchSdkRvDetails,
    fetchEssentialData,
    fetchInitialData,
    fetchAddons,
    // Exposed so the store can seed the photo-fingerprint map from essential-data it
    // consumed via the loader-stub prefetch path; that path bypasses fetchEssentialData
    // (and its internal recordPhotoVersions), so without this the core's map stays empty
    // and fetchPhotosByRvId never appends ?v= (stale-photo cache-bust silently disabled).
    recordPhotoVersions,
  };
}
